import { useMemo } from 'react';
import * as Combobox from '@givebutter/primitives/combobox';
import { faChartNetwork } from '@fortawesome/pro-regular-svg-icons';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import {
    transformContactActivityToReadable,
    transformMultiselectToReadable,
} from 'components/filter/filters-next/readable';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterIcon,
    FilterValue,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import * as Form from 'components/form';
import { ValidationTextMap } from 'components/form/validations';
import { identity } from 'lodash';
import { ActivityType } from 'types/domain';

interface ContactActivityFilterFormValues extends FilterFormValue<ActivityType[]> {}

export const filterKey: ServerKey = 'activities';
export const Label: LabelFilterComponent = () => 'Activity';

export const Icon: FilterIcon = faChartNetwork;

export const Filter: ContentFilterComponent = () => {
    const { defaultValue, ...rest } = useFilter<ActivityType[]>();
    const methods = Form.useForm<ContactActivityFilterFormValues>({
        defaultValues: {
            value: defaultValue ? defaultValue.value : [],
        },
        mode: 'onChange',
    });
    const { control, path } = methods;

    const ctx = useMemo(
        () => ({
            defaultValue,
            ...rest,
        }),
        [defaultValue, rest],
    );

    return (
        <FilterFormContext.Provider value={ctx}>
            <Form.Provider {...methods}>
                <FilterForm>
                    <FilterContainer>
                        <Form.Container>
                            <Form.Label name={path('value')}>Activity</Form.Label>
                            <Form.Combobox
                                control={control}
                                name="value"
                                rules={{ required: ValidationTextMap.Required }}
                            >
                                <FilterComboboxItems>
                                    <Combobox.Item value="email">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Email</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="phone_call">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Phone call</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="note">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Note</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="email.subscribed">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Email subscribed</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="email.unsubscribed">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Email unsubscribed</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="sms.subscribed">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>SMS subscribed</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="sms.unsubscribed">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>SMS unsubscribed</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="transaction.succeeded">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Transaction</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="recurring_plan.created">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Recurring plan</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="campaign.joined">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Joined campaign</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="ticket">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Ticket</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="signup_form.submitted">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Signup form</Combobox.ItemText>
                                    </Combobox.Item>
                                </FilterComboboxItems>
                            </Form.Combobox>
                            <Form.Error name={path('value')} />
                        </Form.Container>
                        <FilterSubmit />
                    </FilterContainer>
                </FilterForm>
            </Form.Provider>
        </FilterFormContext.Provider>
    );
};

export const Readable: ReadableFilterComponent = () => {
    const currentFilter = useCurrentFilter<ActivityType[]>();
    const value = currentFilter?.value ?? [];

    return (
        <ReadableFilterValue>
            {transformMultiselectToReadable(value, {
                transform: v => transformContactActivityToReadable(v),
            })}
        </ReadableFilterValue>
    );
};

export const downstream: DownstreamFilterTransform<ActivityType> = identity;

export const upstream: UpstreamFilterTransform<ActivityType> = (filter: FilterValue) => ({
    value: filter.value,
});
